import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ChevronLeft, Share } from "lucide-react";
import { Header } from "../components/layout/Header";
import { SafeArea } from "../components/layout/SafeArea";
import { Rating } from "../components/ui/Rating";
import { AppIcon } from "../components/ui/AppIcon";
import { GetButton } from "../components/ui/GetButton";
import { AppRow } from "../components/market/AppRow";
import { SectionHeader } from "../components/market/SectionHeader";
import { useScrolled } from "../hooks/useScrolled";
import { topCharts, screenshotUrl } from "../data/apps";

export function AppDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scrolled = useScrolled(60);

  const app = topCharts.find((a) => a.id === Number(id));
  if (!app) return <Navigate to="/" replace />;

  const priceLabel = app.price ? `$${app.price}` : "Get";

  const stats = [
    {
      label: `${app.ratingCount} Ratings`,
      value: app.rating.toFixed(1),
      extra: <Rating value={app.rating} size={10} />,
    },
    { label: "Age", value: "4+", extra: <span className="text-xs text-neutral-500">Years Old</span> },
    { label: "Chart", value: `#${app.rank}`, extra: <span className="text-xs text-neutral-500">{app.subtitle}</span> },
  ];

  return (
    <div className="min-h-dvh bg-white text-neutral-900">
      <Header
        title={scrolled ? app.title : undefined}
        left={
          <button onClick={() => navigate(-1)} className="-ml-2 flex items-center text-[17px] text-[#007AFF]">
            <ChevronLeft size={28} strokeWidth={2.5} />
            Back
          </button>
        }
        right={scrolled ? <GetButton size="sm" variant="primary" label={priceLabel} /> : null}
      />

      <SafeArea>
        <main className="pb-8">
          {/* Верх: иконка + название + Get */}
          <div className="flex gap-4 px-4 pt-3">
            <AppIcon src={app.icon} alt={app.title} className="h-[100px] w-[100px]" />
            <div className="flex min-w-0 flex-1 flex-col justify-between">
              <div>
                <h1 className="truncate text-[22px] font-bold leading-tight">{app.title}</h1>
                <p className="text-[13px] text-neutral-500">{app.subtitle}</p>
              </div>
              <div className="flex items-center justify-between">
                <GetButton variant="primary" label={priceLabel} />
                <button aria-label="Share" className="p-1 text-[#007AFF] transition active:opacity-50">
                  <Share size={22} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>

          {/* Полоска со статистикой */}
          <div className="mt-5 flex divide-x divide-black/10 overflow-x-auto border-y border-black/10 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {stats.map((s) => (
              <div key={s.label} className="flex min-w-[110px] flex-1 shrink-0 flex-col items-center gap-0.5 px-3">
                <span className="text-[11px] font-semibold uppercase text-neutral-500">{s.label}</span>
                <span className="text-xl font-semibold text-neutral-600">{s.value}</span>
                {s.extra}
              </div>
            ))}
          </div>

          {/* Скриншоты */}
          <div className="mt-5 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[1, 2, 3].map((n) => (
              <img
                key={n}
                src={screenshotUrl(app.title, n)}
                alt={`${app.title} screenshot ${n}`}
                className="aspect-[9/19.5] w-52 shrink-0 snap-start rounded-[20px] border border-black/10 object-cover"
              />
            ))}
          </div>

          {/* Описание */}
          <section className="mt-6 px-4">
            <p className="line-clamp-3 text-[15px] leading-snug text-neutral-800">
              Run, jump and explore in a handcrafted pixel world. Unlock new levels, collect rare items and challenge your friends
              in daily races. New content is added every week, so there is always something to discover.
            </p>
            <button className="mt-1 text-[15px] text-[#007AFF]">more</button>
          </section>

          {/* Похожие */}
          <section className="mt-6">
            <div className="px-4">
              <SectionHeader title="You Might Also Like" />
            </div>
            <div>
              {topCharts
                .filter((a) => a.id !== app.id)
                .map((a) => (
                  <AppRow
                    key={a.id}
                    id={a.id}
                    icon={a.icon}
                    title={a.title}
                    subtitle={a.subtitle}
                    rating={a.rating}
                    ratingCount={a.ratingCount}
                    price={a.price}
                  />
                ))}
            </div>
          </section>
        </main>
      </SafeArea>
    </div>
  );
}